import React, { useMemo, useCallback, useState } from "react";
import {
  Group,
  Text,
  Popover,
  Stack,
  Box,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";

import { useResponsiveDatePickerProps, SelectField } from "@/components/generic/form";
import { useTranslation } from "react-i18next";

import {
  toDayjs,
  toDatePickerString,
  getComparisonPeriodDates,
  formatUTCDateRange,
  type PeriodComparisonType,
} from "@em/utils";

import { AppIcon } from "@/components/generic/AppIcon";

// ============================================
// Types
// ============================================

// Re-export for convenience
export type { PeriodComparisonType };

export interface ComparisonPeriod {
  type: PeriodComparisonType;
  dateFrom: string | null;
  dateTo: string | null;
}

export interface PeriodComparisonPickerProps {
  /** The start date of the primary period (ISO string or Date) */
  primaryDateFrom: string | Date | null;

  /** The end date of the primary period (ISO string or Date) */
  primaryDateTo: string | Date | null;

  /** Currently selected comparison type */
  value: PeriodComparisonType;

  /** Callback fired when comparison selection changes */
  onChange: (comparison: ComparisonPeriod) => void;

  /** Custom date for "custom" comparison type (ISO string or Date) */
  customDateFrom?: string | Date | null;

  /** Whether the picker is disabled */
  disabled?: boolean;

  /** Size of the selector */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Label for the field */
  label?: string;

  /** Whether to show "No comparison" option */
  showNoneOption?: boolean;
}

// ============================================
// Component
// ============================================

const PeriodComparisonPickerComponent: React.FC<PeriodComparisonPickerProps> = ({
  primaryDateFrom,
  primaryDateTo,
  value,
  onChange,
  customDateFrom,
  disabled = false,
  size = "sm",
  label,
  showNoneOption = true,
}) => {
  const { t } = useTranslation(["reports", "common"]);
  const responsiveDatePicker = useResponsiveDatePickerProps();
  const [popoverOpened, setPopoverOpened] = useState(false);

  // Build options based on available data
  const options = useMemo(() => {
    const opts: { value: string; label: string }[] = [];

    if (showNoneOption) {
      opts.push({
        value: "none",
        label: t("reports:fuelEconomy.comparison.none", "No comparison"),
      });
    }

    opts.push(
      {
        value: "previous",
        label: t("reports:fuelEconomy.comparison.previous", "Previous Period"),
      },
      {
        value: "previousMonth",
        label: t("reports:fuelEconomy.comparison.previousMonth", "Previous Month"),
      },
      {
        value: "sameMonthLastYear",
        label: t("reports:fuelEconomy.comparison.sameMonthLastYear", "Same Month Last Year"),
      },
      {
        value: "custom",
        label: t("reports:fuelEconomy.comparison.custom", "Custom Period"),
      }
    );

    return opts;
  }, [showNoneOption, t]);

  // Calculate comparison dates for display
  const comparisonDates = useMemo(() => {
    return getComparisonPeriodDates(
      value,
      primaryDateFrom,
      primaryDateTo,
      customDateFrom
    );
  }, [value, primaryDateFrom, primaryDateTo, customDateFrom]);

  // Handle type selection change
  const handleTypeChange = useCallback(
    (newType: string | null) => {
      if (!newType) return;

      const type = newType as PeriodComparisonType;

      if (type === "custom") {
        // Open popover for custom date selection
        setPopoverOpened(true);
        // Don't fire onChange yet - wait for date selection
        return;
      }

      const dates = getComparisonPeriodDates(
        type,
        primaryDateFrom,
        primaryDateTo
      );

      onChange({
        type,
        ...dates,
      });
    },
    [primaryDateFrom, primaryDateTo, onChange]
  );

  // Handle custom date selection
  const handleCustomDateChange = useCallback(
    (dateStr: string | null) => {
      if (!dateStr) return;

      const dates = getComparisonPeriodDates(
        "custom",
        primaryDateFrom,
        primaryDateTo,
        dateStr
      );

      onChange({
        type: "custom",
        ...dates,
      });

      setPopoverOpened(false);
    },
    [primaryDateFrom, primaryDateTo, onChange]
  );

  // Calculate max date for custom picker (must end before primary period starts)
  const maxCustomDate = useMemo((): string | undefined => {
    const from = toDayjs(primaryDateFrom);
    const to = toDayjs(primaryDateTo);
    if (!from || !to) return undefined;
    const periodDays = to.diff(from, "day");
    // Custom period must end before primary starts
    return from.subtract(periodDays + 1, "day").format("YYYY-MM-DD");
  }, [primaryDateFrom, primaryDateTo]);

  // Convert customDateFrom to DatePickerInput format
  const customDateValue = useMemo(
    () => toDatePickerString(customDateFrom),
    [customDateFrom]
  );

  // Check if we have a valid primary period
  const hasPrimaryPeriod = useMemo(
    () => !!toDayjs(primaryDateFrom) && !!toDayjs(primaryDateTo),
    [primaryDateFrom, primaryDateTo]
  );

  return (
    <Box>
      <Popover
        opened={popoverOpened}
        onChange={setPopoverOpened}
        position="bottom-start"
        withinPortal
      >
        <Popover.Target>
          <Box>
            <SelectField
              label={label}
              size={size}
              value={value}
              onChange={(_name, v) => handleTypeChange(v)}
              data={options}
              disabled={disabled || !hasPrimaryPeriod}
              leftSection={<AppIcon iconName="ArrowsLeftRight" size={18} />}
              placeholder={t("reports:fuelEconomy.comparison.placeholder", "Compare to...")}
              allowDeselect={false}
            />
          </Box>
        </Popover.Target>

        <Popover.Dropdown>
          <Stack gap="sm">
            <Text size="md" fw={500}>
              {t("reports:fuelEconomy.comparison.selectCustomDate", "Select comparison start date")}
            </Text>
            <DatePickerInput
              value={customDateValue}
              onChange={handleCustomDateChange}
              maxDate={maxCustomDate}
              size={size}
              placeholder={t("common:form.selectDate", "Select date")}
              popoverProps={{ withinPortal: false }}
              {...responsiveDatePicker}
            />
            <Text size="xs" c="dimmed">
              {t(
                "reports:fuelEconomy.comparison.customHint",
                "The comparison period will have the same length as your selected period."
              )}
            </Text>
          </Stack>
        </Popover.Dropdown>
      </Popover>

      {/* Show calculated comparison period dates */}
      {value !== "none" && comparisonDates.dateFrom && comparisonDates.dateTo && (
        <Group gap="xs" mt={4}>
          <AppIcon iconName="Calendar" size={14} color="dimmed" />
          <Text size="xs" c="dimmed">
            {formatUTCDateRange(comparisonDates.dateFrom, comparisonDates.dateTo)}
          </Text>
        </Group>
      )}
    </Box>
  );
};

export const PeriodComparisonPicker = React.memo(PeriodComparisonPickerComponent);